/**
 * Phase 0 · Infra-1 · P0.CLI.3
 *
 * Thin client for POST /api/video/token.
 *
 * The mobile client always authenticates as a patient (Supabase JWT).
 * Sponsor-link flow is web-only and lives in the /meet sponsor room.
 *
 * See documentation/01. Platform/SYS_VIDEO_CALLING.md §6.1
 * (client) and §10 (phasing). Use case lives in
 * documentation/13. Telemedicine/FEAT_TELEMEDICINE.md.
 */

import Constants from 'expo-constants';
import { Platform } from 'react-native';
import { supabase } from '@/lib/supabase';

export interface VideoTokenRequest {
  sessionType: 'appointment' | 'group_encounter' | 'echo' | string;
  sessionId: string;
  /**
   * Optional. Caller-provided JWT (typically from AuthContext.session.access_token).
   * When supplied, we skip the supabase.auth.getSession() probe entirely — that
   * call is known to hang on cold deep-link launches.
   */
  accessToken?: string;
  /**
   * Optional. Patient or sponsor signed link token from a /meet deep link or
   * the web sponsor room. When present, this takes precedence over the JWT
   * path: we send it as `X-Video-Link-Token` and skip the bearer entirely.
   *
   * The server's resolveSubject middleware peeks at the payload's `typ` claim
   * to pick the right verification secret (patient vs sponsor link).
   */
  linkToken?: string;
}

export interface VideoTokenResponse {
  token: string;
  channel: string;
  uid: number;
  appId: string;
  expiresAt: number;
}

export class VideoTokenError extends Error {
  code: string;
  status: number;
  constructor(code: string, status: number, message?: string) {
    super(message || code);
    this.code = code;
    this.status = status;
  }
}

function getApiBaseUrl(): string {
  const extra = (Constants.expoConfig?.extra ?? {}) as Record<string, unknown>;
  const fromExtra = typeof extra.apiBaseUrl === 'string' ? extra.apiBaseUrl : undefined;
  return (
    fromExtra ||
    process.env.EXPO_PUBLIC_API_BASE_URL ||
    'https://mobile.padrinho.app'
  );
}

function clientSurface(): 'mobile_ios' | 'mobile_android' | 'web' {
  if (Platform.OS === 'ios') return 'mobile_ios';
  if (Platform.OS === 'android') return 'mobile_android';
  return 'web';
}

/**
 * Best-effort decode of a JWT payload. We do NOT verify the signature here —
 * the server is the only authority. We only peek so the deep-link handler can
 * pre-fill `sessionId` from the token's `sid` claim and avoid an extra round
 * trip. If decoding fails or the shape is wrong, we return null and the call
 * screen will surface a user-facing error after the server rejects the token.
 *
 * Pure function, no native deps. Works under Hermes.
 */
export function peekLinkTokenPayload(token: string): {
  sid?: string;
  sub?: string;
  typ?: string;
  exp?: number;
} | null {
  if (typeof token !== 'string') return null;
  const parts = token.split('.');
  if (parts.length !== 3) return null;
  try {
    // base64url → base64
    const padded = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const base64 = padded + '==='.slice((padded.length + 3) % 4);
    // global.atob is available in Hermes + the web bundle.
    const json =
      typeof atob === 'function'
        ? atob(base64)
        : // eslint-disable-next-line @typescript-eslint/no-var-requires
          Buffer.from(base64, 'base64').toString('utf8');
    return JSON.parse(json);
  } catch {
    return null;
  }
}

/**
 * Request an Agora RTC token for the given session. Throws {@link VideoTokenError}
 * for any non-2xx response. The server decides the channel name — the client
 * never gets to choose it.
 */
export async function requestVideoToken(
  req: VideoTokenRequest
): Promise<VideoTokenResponse> {
  if (!req?.sessionType || !req?.sessionId) {
    throw new VideoTokenError('missing_fields', 400, 'sessionType and sessionId required');
  }

  // Build auth headers. A signed link token (from a /meet deep link) wins
  // outright — we don't even reach for the Supabase session in that case,
  // which is what makes the cold-start deep-link path fast and deadlock-free.
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };

  if (req.linkToken) {
    headers['X-Video-Link-Token'] = req.linkToken;
  } else {
    // Prefer caller-supplied access token (from AuthContext). supabase.auth.getSession()
    // is known to hang on cold deep-link launches — only fall back to it when no
    // token was passed in, and even then race it against a hard timeout so the
    // call screen can never spin forever without an error.
    let accessToken: string | undefined = req.accessToken;
    if (!accessToken) {
      const sessionPromise = supabase.auth.getSession();
      const timeoutPromise = new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error('session_timeout')), 5000)
      );
      try {
        const result: any = await Promise.race([sessionPromise, timeoutPromise]);
        accessToken = result?.data?.session?.access_token;
      } catch (err: any) {
        if (err?.message === 'session_timeout') {
          throw new VideoTokenError(
            'session_timeout',
            408,
            'Supabase session restore timed out (>5s)'
          );
        }
        throw err;
      }
    }

    if (!accessToken) {
      throw new VideoTokenError('not_authenticated', 401, 'No Supabase session');
    }
    headers.Authorization = `Bearer ${accessToken}`;
  }

  const url = `${getApiBaseUrl()}/api/video/token`;
  const response = await fetch(url, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      sessionType: req.sessionType,
      sessionId: req.sessionId,
      clientSurface: clientSurface(),
    }),
  });

  let body: any = null;
  try {
    body = await response.json();
  } catch {
    // fall through
  }

  if (!response.ok || !body?.success) {
    const code = body?.error || `http_${response.status}`;
    throw new VideoTokenError(code, response.status, code);
  }

  return {
    token: body.token,
    channel: body.channel,
    uid: body.uid,
    appId: body.appId,
    expiresAt: body.expiresAt,
  };
}
